import { List } from "immutable";
import { isNil, isString } from "lodash";

import { colorMap } from "../constants/colorOptions";
import { DateFormatIdentifiers } from "../constants/dateFormats";
import { CSV_EXPORT_FIELDS, CSV_FIELD_LABELS, CSV_SECOND_LEVEL_DELIMITER } from "../constants/importExportFile";
import { PropertyFormatIdentifier } from "../constants/propertyFormats";
import { TimeFormatIdentifiers } from "../constants/timeFormats";
import { displayItemSelectors } from "../features/sharedSelectors/displayItems";
import { attachmentSelectors } from "../modules/attachments";
import { calendarSelectors } from "../modules/definition/calendar";
import { isDateValid } from "../modules/definition/calendar/dateFunctions";
import { stringFromDate } from "../modules/definition/calendar/dateFunctions/stringFromDate";
import { stringFromTimespan } from "../modules/definition/calendar/dateFunctions/timespanFunctions";
import { colorSelectors } from "../modules/definition/colors";
import { narrativeSettingsSelectors } from "../modules/definition/narrativeSettings";
import { propertySelectors } from "../modules/definition/properties";
import { referenceSelectors } from "../modules/definition/references";
import { typeSelectors } from "../modules/definition/types";
import { dependencySelectors } from "../modules/dependencies";
import displayCacheSelectors from "../modules/display/selectors";
import { itemSelectors } from "../modules/items";
import { relationshipSelectors } from "../modules/relationships";
import { tagSelectors } from "../modules/tags";
import { uiStateSelectors } from "../modules/uiState";
import { Precision } from "../types";
import calendarTypes from "../types/calendarTypes";

const getExistingReferenceIds = (itemsList, relationshipsById, referencesById) => {
  return itemsList.reduce((rList, i) => {
    let newList = rList;
    i.references.forEach(rId => {
      const refId = referencesById.get(relationshipsById.get(rId).reference).id;
      if (!newList.includes(refId)) {
        newList = newList.push(refId);
      }
    });
    return newList;
  }, List());
};

const getReferenceFields = (referenceIds, referencesById) => {
  return referenceIds.map(id => referencesById.get(id).label);
};

const getExistingPropertyIds = itemsList => {
  return itemsList.reduce((pList, i) => {
    let newList = pList;
    i.propertyValues.forEach((v, pId) => {
      if (!newList.includes(pId)) {
        newList = newList.push(pId);
      }
    });
    return newList;
  }, List());
};

const getPropertyFields = (propertyIds, propertiesById) => {
  return propertyIds.map(id => propertiesById.get(id).label);
};

const getCsvFieldsLine = (delimiter, referenceIds, referencesById, propertyIds, propertiesById) => {
  // Normal fields
  let fieldsList = List(CSV_EXPORT_FIELDS.map(f => CSV_FIELD_LABELS[f]));
  // References
  fieldsList = fieldsList.concat(getReferenceFields(referenceIds, referencesById));
  // Properties
  fieldsList = fieldsList.concat(getPropertyFields(propertyIds, propertiesById));
  // Add new line
  return fieldsList.join(delimiter) + "\n";
};

// In case the pass in value would be not a string
const escapeDoubleQuotes = str => (isString(str) ? str.replace(/"/g, `""`) : str);

const wrapValueWithQuotes = value => (isNil(value) ? "" : `"${escapeDoubleQuotes(value)}"`);

const getItemCSVId = (item, typesById) => {
  const type = typesById.get(item.type);
  if (typeSelectors.isShortLabelType(type)) {
    return itemSelectors.getShortLabelForItem(item);
  } else if (typeSelectors.isAvatarType(type)) {
    return item.label;
  }
  return itemSelectors.getInitialsForItem(item);
};

const getListValuesString = list => wrapValueWithQuotes(list.join(CSV_SECOND_LEVEL_DELIMITER));

const getDateString = (calendar, date) => {
  let result = "";
  if (isDateValid(date)) {
    if (calendar.calendarType === calendarTypes.CALENDAR_TYPE_DATE_TIME) {
      // Omit unused precisions
      const eraPart = date.era === calendar.get("eras").count() - 1 ? "" : "%E ";
      let format = `${eraPart}%Y`;
      if (date.precision >= Precision.Month) {
        format += "-%m";
      }
      if (date.precision >= Precision.Day) {
        format += "-%d";
      }
      if (date.precision >= Precision.Hour) {
        format += " %H:%M";
      }
      if (date.precision >= Precision.Second) {
        format += ":%S";
      }
      result = stringFromDate(calendar, date, {
        dateFormat: DateFormatIdentifiers.Custom,
        customFormat: format
      });
    } else {
      result = stringFromDate(calendar, date, { timeFormat: TimeFormatIdentifiers.TwentyFour, isCSVFormat: true });
    }
  }
  return result;
};

const getInitials = (item, typesById) => {
  return typeSelectors.isAvatarType(typesById.get(item.type)) ? itemSelectors.getInitialsForItem(item) : item.initials;
};

const getItemNormalValues = (
  item,
  typesById,
  itemsById,
  dependenciesById,
  attachmentsById,
  tagsMap,
  calendar,
  narrativeLabels,
  colorsById
) => {
  return List(CSV_EXPORT_FIELDS).map(field => {
    const value = item[field];
    switch (field) {
      // Direct values
      case "displayId":
      case "label":
      case "summary":
        return wrapValueWithQuotes(value);
      case "initials":
        return getInitials(item, typesById);
      case "narrativePosition":
        return item.narrativePathLabel ? wrapValueWithQuotes(item.narrativePathLabel) : "";
      // Values to transform
      case "type":
        return typesById.get(value).label;
      case "color": {
        const namedColor = colorsById.find(c => c.swatchColor === value);
        if (namedColor) {
          return namedColor.label;
        }
        const hex = colorMap[value];
        return hex ? hex : value ? value : null;
      }
      case "super": {
        const superItem = itemsById.get(value);
        return superItem ? getItemCSVId(superItem, typesById) : "";
      }
      case "ongoing":
        return value ? "True" : "False";
      // Dates
      case "startDate":
      case "latestStartDate":
      case "earliestEndDate":
      case "endDate": {
        return getDateString(calendar, value);
      }
      // Durations
      case "duration": {
        return wrapValueWithQuotes(stringFromTimespan(value));
      }
      case "blockedByDependencies": {
        const depSubjectsList = value.map(dId => {
          const dep = dependenciesById.get(dId);
          return getItemCSVId(itemsById.get(dep.subject), typesById);
        });
        return getListValuesString(depSubjectsList);
      }
      case "links": {
        const attachPathsList = value.map(aId => {
          return attachmentsById.get(aId).path;
        });
        return getListValuesString(attachPathsList);
      }
      case "tags": {
        const tagsList = value.map(tId => {
          return tagsMap.get(tId);
        });
        return getListValuesString(tagsList);
      }
      default:
        return "";
    }
  });
};

const getItemReferenceValues = (item, referenceIds, relationshipsById, itemsById, typesById) => {
  const itemRefs = item.references.map(rId => relationshipsById.get(rId));
  // Each reference id is a field
  return referenceIds.map(refId => {
    // Find out all relationships belonging to this reference
    // Convert the relationships to the id of the other item in it
    const refValues = itemRefs
      .filter(iRef => iRef.reference === refId)
      .map(iRef => {
        const targetItemId = iRef.subject === item.id ? iRef.object : iRef.subject;
        return getItemCSVId(itemsById.get(targetItemId), typesById);
      });
    return getListValuesString(refValues);
  });
};

const getPropertyValue = (item, pId, propertiesById) => {
  const value = item.propertyValues.get(pId);
  const property = propertiesById.get(pId);
  if (property.format === PropertyFormatIdentifier.list) {
    const foundOption = property.allowed.find(o => o.id === value);
    return foundOption ? foundOption.label : "";
  }
  return value;
};

const getItemPropertyValues = (item, propertyIds, propertiesById) => {
  return propertyIds.map(pId => {
    return wrapValueWithQuotes(getPropertyValue(item, pId, propertiesById));
  });
};

const generateItemValuesLine = (
  delimiter,
  item,
  typesById,
  itemsById,
  dependenciesById,
  attachmentsById,
  tagsMap,
  calendar,
  referenceIds,
  relationshipsById,
  propertyIds,
  propertiesById,
  narrativeLabels,
  colorsById
) => {
  // Normal field values
  const normalValues = getItemNormalValues(
    item,
    typesById,
    itemsById,
    dependenciesById,
    attachmentsById,
    tagsMap,
    calendar,
    narrativeLabels,
    colorsById
  );
  // Reference field values
  const referenceValues = getItemReferenceValues(item, referenceIds, relationshipsById, itemsById, typesById);
  // Property field values
  const propertyValues = getItemPropertyValues(item, propertyIds, propertiesById);
  // Concat all values
  const result = normalValues.concat(referenceValues, propertyValues).join(delimiter);
  return result;
};

const getCsvItemLines = (
  delimiter,
  itemsList,
  typesById,
  itemsById,
  dependenciesById,
  attachmentsById,
  tagsMap,
  calendar,
  referenceIds,
  relationshipsById,
  propertyIds,
  propertiesById,
  narrativeLabels,
  colorsById
) => {
  return itemsList
    .map(i =>
      generateItemValuesLine(
        delimiter,
        i,
        typesById,
        itemsById,
        dependenciesById,
        attachmentsById,
        tagsMap,
        calendar,
        referenceIds,
        relationshipsById,
        propertyIds,
        propertiesById,
        narrativeLabels,
        colorsById
      )
    )
    .join("\n");
};

const getItemsToExport = (isSelectedItemsOnly, typeIds, state) => {
  // Selected or all
  const items = isSelectedItemsOnly
    ? uiStateSelectors.getSelectedItems(state).map(id => displayCacheSelectors.getDisplayItemsById(state).get(id))
    : displayItemSelectors.getDisplayItemsList(state);
  // Filter by types
  return items.filter(i => typeIds.includes(i.type));
};

const generateCsvString = (state, delimiter, typeIds, isSelectedItemsOnly) => {
  let resultStr = "";
  const itemsToExport = getItemsToExport(isSelectedItemsOnly, typeIds, state);
  // State data
  const relationshipsById = relationshipSelectors.getRelationshipsById(state);
  const referencesById = referenceSelectors.getReferencesById(state);
  const propertiesById = propertySelectors.getPropertiesById(state);
  const typesById = typeSelectors.getTypesById(state);
  const itemsById = itemSelectors.getItemsById(state);
  const dependenciesById = dependencySelectors.getDependenciesById(state);
  const attachmentsById = attachmentSelectors.getAttachmentsById(state);
  const tagsMap = tagSelectors.getTags(state);
  const calendar = calendarSelectors.getCalendar(state);
  const narrativeLabels = narrativeSettingsSelectors.getNarrativeLabels(state);
  const colorsById = colorSelectors.getColorsById(state);
  // Existing References, Properties
  const exportReferenceIds = getExistingReferenceIds(itemsToExport, relationshipsById, referencesById);
  const exportPropertyIds = getExistingPropertyIds(itemsToExport);
  // Fields line
  resultStr += getCsvFieldsLine(delimiter, exportReferenceIds, referencesById, exportPropertyIds, propertiesById);
  // item lines
  resultStr += getCsvItemLines(
    delimiter,
    itemsToExport,
    typesById,
    itemsById,
    dependenciesById,
    attachmentsById,
    tagsMap,
    calendar,
    exportReferenceIds,
    relationshipsById,
    exportPropertyIds,
    propertiesById,
    narrativeLabels,
    colorsById
  );
  return resultStr;
};

export { generateCsvString };
